import type { Editor } from '@tiptap/react';
import { useState } from 'react';
import { compileSectionDocx } from '../../../services/tenderAIService';
import { getTenderFileDownloadUrl } from '../../../services/tenderFileService';
import { setTenderSectionSuggestion } from '../../../services/tenderSectionService';
import type { User } from '../../../types/auth';
import type { Section, Tender } from '../../../types/tender';
import { logDocumentActivity } from '../../../utils/activityLogger';
import { extractTitleTags } from '../ReviewSections/utils';

export function useSectionEditing(
  account: User | null,
  tenderId: string,
  tender: Tender,
  sections: Section[],
  saveContent: (section: Section, content: string) => Promise<boolean>,
  resolveSuggestion: (section: Section, accept: boolean, finalContent?: string) => Promise<void>,
  updateSectionState: (sectionId: string, updates: Partial<Section>) => void,
  pushToHistory: (sectionId: string, currentContent: string, currentSuggested?: string) => void,
  showSnackbar: (msg: string, severity?: 'success' | 'info' | 'warning' | 'error') => void,
  lock: (section: Section) => Promise<void>,
  unlock: (section: Section) => Promise<void>,
  editingSectionId: string | null,
  setEditingSectionId: (id: string | null) => void,
  editContent: string,
  setEditContent: (content: string) => void,
  showDiffs: Record<string, boolean>,
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
) {
  const [downloadingSectionId, setDownloadingSectionId] = useState<string | null>(null);
  const [activeEditor, setActiveEditor] = useState<Editor | null>(null);

  const [chatSectionId, setChatSectionId] = useState<string | null>(null);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatContextContent, setChatContextContent] = useState<string>('');
  const [inlineResolved, _setInlineResolved] = useState<Record<string, boolean>>({});

  const handleOpenManualEdit = async (section: Section) => {
    if (!section.lockedBy || section.lockedBy.uid === account?.uid) {
      await lock(section);
      setEditingSectionId(section.id);
      setEditContent(section.content);
      setShowDiffs((prev) => ({ ...prev, [section.id]: false }));
    } else {
      showSnackbar(`Section is locked by ${section.lockedBy.displayName}`, 'error');
    }
  };

  const handleSave = async (section: Section) => {
    pushToHistory(section.id, section.content, section.suggested_content);
    const success = await saveContent(section, editContent);
    if (success) {
      setEditingSectionId(null);
      setActiveEditor(null);
      await unlock(section);
    }
    return success;
  };

  const handleOpenAIEdit = async (section: Section) => {
    if (editingSectionId === section.id) {
      await handleSave(section);
    } else if (editingSectionId) {
      setEditingSectionId(null);
    }

    if (!section.lockedBy || section.lockedBy.uid === account?.uid) {
      await lock(section);
      setChatSectionId(section.id);
      setChatContextContent(section.content || '');
    } else {
      showSnackbar(`Section is locked by ${section.lockedBy.displayName}`, 'error');
    }
  };

  const handleCancelEdit = async (section: Section) => {
    setEditingSectionId(null);
    setActiveEditor(null);
    setChatSectionId(null);
    await unlock(section);
  };

  const handleApplyAIEdit = async (newContent: string) => {
    if (!chatSectionId) {
      return;
    }
    const section = sections.find((s) => s.id === chatSectionId);
    if (!section) {
      return;
    }

    pushToHistory(section.id, section.content, section.suggested_content);
    const success = await saveContent(section, newContent);
    if (success) {
      if (editingSectionId === section.id) {
        setEditContent(newContent);
      }
      showSnackbar('Section updated from AI suggestion', 'success');
    }
  };

  const handleReceiveSuggestion = async (suggestion: string) => {
    if (!chatSectionId) {
      return;
    }
    const section = sections.find((s) => s.id === chatSectionId);
    if (!section) {
      return;
    }

    pushToHistory(section.id, section.content, section.suggested_content);
    await setTenderSectionSuggestion(tenderId, section.id, suggestion);
    updateSectionState(section.id, { suggested_content: suggestion });
    showSnackbar('Suggestion added to section. Please review.', 'info');
  };

  const handleAcceptSuggestion = (section: Section) => {
    pushToHistory(section.id, section.content, section.suggested_content);
    resolveSuggestion(section, true, section.suggested_content);
  };

  const handleRejectSuggestion = (section: Section) => {
    pushToHistory(section.id, section.content, section.suggested_content);
    resolveSuggestion(section, false);
  };

  const handleDiffApply = async (sectionId: string, newOld: string, newNew: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) {
      return;
    }

    pushToHistory(sectionId, section.content, section.suggested_content);
    const contentPromise = saveContent(section, newOld);
    const suggestionPromise =
      newOld === newNew
        ? resolveSuggestion(section, false)
        : setTenderSectionSuggestion(tenderId, sectionId, newNew);

    await Promise.all([contentPromise, suggestionPromise]);
    updateSectionState(sectionId, {
      content: newOld,
      suggested_content: newOld === newNew ? undefined : newNew,
    });
  };

  const handleDownloadSectionDocx = async (section: Section) => {
    setDownloadingSectionId(section.id);
    try {
      const clientId = tender.companyId || account?.clientId;
      if (!clientId) {
        showSnackbar('Error: Missing Client ID', 'error');
        return;
      }
      const response = await compileSectionDocx(tenderId, section.id, clientId);
      if (response.success && response.path) {
        const safeTitle = extractTitleTags(section.title).cleanTitle.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `${safeTitle}.docx`;

        const downloadUrl = await getTenderFileDownloadUrl(response.path);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSnackbar('Section downloaded successfully', 'success');
        logDocumentActivity.downloaded({
          type: 'section_docx',
          fileName: fileName,
          source: `tender_details/${tenderId}/section/${section.id}`,
        });
      } else {
        showSnackbar('Failed to generate DOCX', 'error');
      }
    } catch (e: unknown) {
      const err = e as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Error downloading section';
      showSnackbar(msg, 'error');
    } finally {
      setDownloadingSectionId(null);
    }
  };

  return {
    editingSectionId,
    setEditingSectionId,
    downloadingSectionId,
    editContent,
    setEditContent,
    showDiffs,
    setShowDiffs,
    activeEditor,
    setActiveEditor,
    chatSectionId,
    setChatSectionId,
    isChatMinimized,
    setIsChatMinimized,
    chatContextContent,
    inlineResolved,
    handleOpenManualEdit,
    handleSave,
    handleOpenAIEdit,
    handleCancelEdit,
    handleApplyAIEdit,
    handleReceiveSuggestion,
    handleAcceptSuggestion,
    handleRejectSuggestion,
    handleDiffApply,
    handleDownloadSectionDocx,
  };
}
